import { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarBlank, MapPin, Clock } from '@phosphor-icons/react';
import { Calendar } from '../ui/calendar';
import { Skeleton } from '../ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Calendrier des réunions d'un chantier — RP13 (revue produit 2026-09, D15).
 *
 * Ancien onglet chantier « Agenda » : il lisait les comptes rendus et
 * « Planifier une réunion » créait un compte rendu vide (CR fantôme qui consommait
 * un numéro). Il est retiré ; ce calendrier est affiché dans Suivi chantier ›
 * Réunions et ne travaille que sur les réunions (`/chantiers/{id}/evenements`,
 * même objet que l'agenda de l'application terrain) :
 *   - lecture : GET /chantiers/{id}/evenements (ou `events` fournis par le parent) ;
 *   - création : A2 (constat 5) — un seul bouton « Planifier une réunion », celui de
 *     Suivi › Réunions ; le jour choisi ici est remonté au parent (`onDateChange`) pour
 *     pré-remplir le formulaire (POST /chantiers/{id}/evenements).
 * Aucun compte rendu n'est créé ici : un CR se crée volontairement depuis
 * « Comptes rendus ».
 */

const pad = (n) => String(n).padStart(2, '0');

// Valeur d'un champ datetime-local (heure locale) pour le jour choisi, 9 h par défaut.
export const dateLocalePourJour = (jour, heure = 9) => {
  const d = new Date(jour);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(heure)}:00`;
};

const cleJour = (valeur) => new Date(valeur).toDateString();

export default function ProjectPlanning({ chantierId, events: eventsProp, onDateChange, onSelect }) {
  const [date, setDateState] = useState(new Date());
  const setDate = (d) => { setDateState(d); if (onDateChange) onDateChange(dateLocalePourJour(d)); };
  const [fetched, setFetched] = useState([]);
  const [loading, setLoading] = useState(!eventsProp);

  useEffect(() => {
    if (eventsProp) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/evenements`, { withCredentials: true });
        if (alive) setFetched(Array.isArray(data) ? data : []);
      } catch { /* écran vide plutôt qu'une erreur */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [chantierId, eventsProp]);

  const events = (eventsProp || fetched).filter((e) => e && e.date_debut);
  const joursAvecReunion = new Set(events.map((e) => cleJour(e.date_debut)));
  const reunionsDuJour = events
    .filter((e) => cleJour(e.date_debut) === date.toDateString())
    .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-testid="reunions-calendrier">
      <div className="card-kroquis">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => d && setDate(d)}
          className="rounded-none border-0"
          modifiers={{ reunion: (d) => joursAvecReunion.has(d.toDateString()) }}
          modifiersStyles={{ reunion: { backgroundColor: '#FFF3E0', color: '#C75B39', fontWeight: '600' } }}
        />
      </div>

      <div className="card-kroquis">
        <div className="flex items-center justify-between mb-4 gap-2">
          <h4 className="text-base font-semibold text-[#2C2C2C]">
            {date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h4>
        </div>

        {loading ? (
          <div className="space-y-3">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
        ) : reunionsDuJour.length === 0 ? (
          <div className="text-center py-8">
            <CalendarBlank size={40} className="mx-auto text-[#E0E0DB] mb-3" />
            <p className="text-[#6B6B6B] text-sm">Aucune réunion ce jour</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reunionsDuJour.map((evt) => (
              <button
                type="button"
                key={evt.id}
                className="w-full text-left border border-[#E0E0DB] p-3 hover:border-[#2C2C2C] transition-colors"
                onClick={() => onSelect && onSelect(evt)}
                data-testid={`calendrier-reunion-${evt.id}`}
              >
                <p className="font-medium text-[#2C2C2C]">{evt.titre || 'Réunion de chantier'}</p>
                <div className="flex items-center gap-4 mt-1 text-sm text-[#6B6B6B]">
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {new Date(evt.date_debut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {evt.lieu && (
                    <span className="flex items-center gap-1"><MapPin size={14} />{evt.lieu}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
